if (!customElements.get('purchase-confirmation')) {
  customElements.define('purchase-confirmation', class PurchaseConfirmation extends ModalDialog {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.once = this.dataset.once === 'true';
      this.version = this.dataset.version;
      this.storageKey = `purchase-confirm`;
      this.collections = new Set(JSON.parse(this.dataset.collections).map(String));
      this.products = new Map();
      this.requests = new Map();
      this.replaying = new WeakSet();
      this.paymentGrants = new WeakSet();
      this.paymentUpdates = new WeakMap();
      this.checkbox = this.querySelector('input[type="checkbox"]');
      this.errorMessage = this.querySelector('[role="alert"]');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };

      this.querySelector('form').addEventListener('submit', (event) => this.confirm(event), options);
      this.querySelector('[data-purchase-close]').addEventListener('click', () => this.hide(), options);
      this.checkbox.addEventListener('change', () => {
        this.errorMessage.hidden = true;
        this.checkbox.removeAttribute('aria-invalid');
      }, options);
      document.addEventListener('click', (event) => this.handleClick(event), { ...options, capture: true });
      document.addEventListener('submit', (event) => this.handleSubmit(event), { ...options, capture: true });
      document.addEventListener('change', () => this.sync(), options);
      document.addEventListener('PageFlyHelperContentLoaded', () => this.sync(), options);
      document.addEventListener('shopify:section:load', () => this.sync(), options);
      window.addEventListener('storage', (event) => {
        if (event.key === this.storageKey || event.key === null) this.sync();
      }, options);
      window.addEventListener('pageshow', () => this.sync(), options);
      this.observer = new MutationObserver(() => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.sync();
        });
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
      this.sync();
    }

    disconnectedCallback() {
      this.hide();
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.initialized = false;
    }

    hasConfirmed() {
      if (!this.once) return false;
      if (this.storageUnavailable && this.confirmedInMemory) return true;
      try {
        const record = JSON.parse(localStorage.getItem(this.storageKey));
        return record?.confirmed === true && record.version === this.version;
      } catch (error) {
        if (error instanceof SyntaxError) return false;
        return Boolean(this.confirmedInMemory);
      }
    }

    requestConfirmation(opener) {
      if (this.hasConfirmed()) return Promise.resolve(true);
      if (this.resolveConfirmation) return Promise.resolve(false);
      this.checkbox.checked = false;
      this.checkbox.removeAttribute('aria-invalid');
      this.errorMessage.hidden = true;
      this.enableScrollWhenOpen = document.body.hasAttribute('scroll-y-off');
      this.openerControls = opener?.getAttribute('aria-controls');
      opener?.setAttribute('aria-controls', this.id);
      super.show(opener);
      return new Promise((resolve) => { this.resolveConfirmation = resolve; });
    }

    confirm(event) {
      event.preventDefault();
      if (!this.checkbox.checked) {
        this.errorMessage.hidden = false;
        this.checkbox.setAttribute('aria-invalid', 'true');
        this.checkbox.focus();
        return;
      }
      if (this.once) {
        this.confirmedInMemory = true;
        try {
          localStorage.setItem(this.storageKey, JSON.stringify({
            confirmed: true,
            count: 1,
            confirmedAt: new Date().toISOString(),
            version: this.version,
          }));
        } catch {
          this.storageUnavailable = true;
        }
      }
      const resolve = this.resolveConfirmation;
      this.resolveConfirmation = null;
      this.hide();
      this.sync();
      resolve?.(true);
    }

    hide() {
      const resolve = this.resolveConfirmation;
      this.resolveConfirmation = null;
      if (this.hasAttribute('open')) super.hide();
      if (this.openedBy) {
        if (this.openerControls === null) this.openedBy.removeAttribute('aria-controls');
        else if (this.openerControls !== undefined) this.openedBy.setAttribute('aria-controls', this.openerControls);
      }
      resolve?.(false);
    }

    sync() {
      document.querySelectorAll('[data-purchase-product-id]').forEach((element) => {
        this.products.set(element.dataset.purchaseProductId, element.dataset.purchaseRequired === 'true');
      });
      document.querySelectorAll('[data-pf-type="ProductATC"], [data-pf-type="ProductATC2"]').forEach((button) => {
        const requirement = this.requirement(button.form || button.closest('form'), button);
        if (requirement?.then) requirement.catch(() => {});
      });
      document.querySelectorAll('[data-purchase-payment]').forEach((payment) => this.refreshPayment(payment));
    }

    productRequired(id, url) {
      id = String(id || '');
      if (!id) return false;
      if (this.products.has(id)) return this.products.get(id);
      const pageflyCollections = window.__pagefly_product_collections?.[id];
      if (Array.isArray(pageflyCollections)) {
        const required = pageflyCollections.some((collection) => this.collections.has(String(collection.id)));
        this.products.set(id, required);
        return required;
      }
      if (this.requests.has(id)) return this.requests.get(id);
      url = url || window.__pageflyProducts?.[id]?.url;
      if (!url) return false;
      const endpoint = new URL(url, window.location.origin);
      if (endpoint.origin !== window.location.origin) return false;
      if (endpoint.pathname.startsWith('/products/') || endpoint.pathname.startsWith('/collections/')) {
        endpoint.pathname = (window.Shopify?.routes?.root || '/').replace(/\/$/, '') + endpoint.pathname;
      }
      endpoint.searchParams.set('section_id', 'purchase-confirmation-product');
      const request = fetch(endpoint, { signal: this.controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error('Product eligibility unavailable');
          return response.text();
        })
        .then((html) => {
          const product = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-purchase-product-id]');
          if (!product || product.dataset.purchaseProductId !== id) throw new Error('Product eligibility unavailable');
          const required = product.dataset.purchaseRequired === 'true';
          this.products.set(id, required);
          return required;
        })
        .catch((error) => {
          this.requests.delete(id);
          throw error;
        });
      this.requests.set(id, request);
      return request;
    }

    requirement(form, button) {
      const owner = button?.closest('[data-purchase-product-id]') || form?.closest('[data-purchase-product-id]');
      const id = button?.dataset.productId || owner?.dataset.purchaseProductId || form?.dataset.productid;
      if (owner?.dataset.purchaseProductId === String(id)) {
        this.products.set(String(id), owner.dataset.purchaseRequired === 'true');
      }
      const requirements = [this.productRequired(id, owner?.dataset.purchaseUrl)];
      if (requirements[0] === true) return true;
      form?.closest('product-info')?.querySelectorAll('.recommended-product-selector:checked:not(:disabled)').forEach((checkbox) => {
        const item = checkbox.closest('.recommended-product-item');
        requirements.push(this.productRequired(checkbox.dataset.productId, item?.querySelector('a[href]')?.href));
      });
      if (requirements.includes(true)) {
        requirements.forEach((value) => {
          if (value?.then) value.catch(() => {});
        });
        return true;
      }
      if (requirements.some((value) => value?.then)) {
        return Promise.all(requirements).then((values) => values.some(Boolean));
      }
      return false;
    }

    refreshPayment(payment) {
      const form = payment.closest('form');
      const requirement = this.requirement(form);
      const update = {};
      this.paymentUpdates.set(payment, update);
      const apply = (required) => {
        if (!payment.isConnected || this.paymentUpdates.get(payment) !== update) return;
        const allowed = !required || this.hasConfirmed() || this.paymentGrants.has(payment);
        const wallet = payment.querySelector('[data-purchase-wallet]');
        wallet.hidden = !allowed;
        wallet.inert = !allowed;
        payment.querySelector('[data-purchase-payment-trigger]').hidden = allowed;
      };
      if (requirement?.then) {
        apply(true);
        requirement.then(apply).catch(() => {});
      } else {
        apply(requirement);
      }
    }

    handleClick(event) {
      if (!(event.target instanceof Element)) return;
      const wallet = event.target.closest('[data-purchase-wallet]');
      if (wallet) {
        if (!this.once) {
          const payment = wallet.closest('[data-purchase-payment]');
          requestAnimationFrame(() => {
            this.paymentGrants.delete(payment);
            this.refreshPayment(payment);
          });
        }
        return;
      }
      const paymentTrigger = event.target.closest('[data-purchase-payment-trigger]');
      if (paymentTrigger) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const payment = paymentTrigger.closest('[data-purchase-payment]');
        this.proceed(payment.closest('form'), paymentTrigger, () => {
          if (!this.once) this.paymentGrants.add(payment);
          this.refreshPayment(payment);
          const region = payment.querySelector('[data-purchase-wallet]');
          region.setAttribute('tabindex', '-1');
          region.focus();
        });
        return;
      }
      const button = event.target.closest('button[name="add"], input[type="submit"][name="add"], [data-pf-type="ProductATC"], [data-pf-type="ProductATC2"]');
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true' || this.replaying.has(button) || this.hasConfirmed()) return;
      const form = button.form || button.closest('form');
      if (this.replaying.has(form)) return;
      const requirement = this.requirement(form, button);
      if (requirement === false) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.proceed(form, button, () => {
        if (!button.isConnected || button.disabled) return;
        this.replaying.add(button);
        if (form) this.replaying.add(form);
        try { button.click(); } finally {
          this.replaying.delete(button);
          if (form) this.replaying.delete(form);
        }
      }, requirement);
    }

    handleSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]') || this.replaying.has(form) || this.replaying.has(event.submitter) || this.hasConfirmed()) return;
      if (event.submitter?.disabled || form.querySelector('[name="id"]')?.disabled) return;
      const requirement = this.requirement(form, event.submitter);
      if (requirement === false) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.proceed(form, event.submitter || form, () => {
        if (!form.isConnected) return;
        this.replaying.add(form);
        try { form.requestSubmit(event.submitter || undefined); } finally { this.replaying.delete(form); }
      }, requirement);
    }

    async proceed(form, opener, action, requirement = this.requirement(form, opener)) {
      try {
        if (await requirement && !await this.requestConfirmation(opener)) return;
        action();
      } catch (error) {
        if (error.name !== 'AbortError') popToast(this.dataset.loadError, 'error');
      }
    }
  });
}
